/**
 * 图像生成控件（共享块，P2-主体 刀1）。把「模型/参数/实时成本/提示词三态/负向/结果预览+候选/悬停工具栏」
 * 抽出来，供图像节点与主体节点共用，避免两套面板各写各的、以后走样（用户 2026-10-02 要求主体复用图像逻辑）。
 * 只负责渲染与回调，写回 store 由调用方（编辑器）负责。
 */
import { useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Loader2, Sparkles, Wand2 } from 'lucide-react';

import type { CanvasNode, GenerationParams, MediaRef } from '../../model/canvas';
import type { ImageEditOp } from './generationService';
import {
  IMAGE_MODELS,
  estimateImageCredit,
  findImageModel,
  formatDiscount,
} from '../../model/modelRegistry';

const field = 'w-full rounded-control bg-surface-base px-2.5 py-2 text-ui text-ink-primary outline-none';
const sel = 'rounded-control bg-surface-base px-2 py-1 text-uiSm text-ink-primary outline-none';
const linkItem = 'text-ink-secondary transition-colors hover:text-ink-primary hover:underline disabled:opacity-40';
const promptBtn = 'inline-flex items-center gap-1 text-uiSm transition hover:underline disabled:opacity-40';
const toolBtn =
  'rounded-control px-2 py-0.5 text-uiSm text-ink-primary transition-colors hover:bg-accent hover:text-accent-ink disabled:opacity-50';

/** 裁剪预置比例（P-B2）。 */
const CROP_RATIOS: string[] = ['1:1', '16:9', '9:16', '4:3', '3:4'];

/** 灯光预置方位（P-B3）。 */
const LIGHT_PRESETS: string[] = ['左上', '右上', '左下', '右下', '中心', '逆光'];

export interface ImageGenControlsProps {
  node: CanvasNode;
  generating: boolean;
  hasUpstream: boolean;
  onParams: (patch: Partial<GenerationParams>) => void;
  onGenerate: () => void;
  onOptimize: () => void;
  onCompose: () => void;
  onUpload: (file: File) => void;
  onPickVariant: (media: MediaRef) => void;
  onEdit: (op: ImageEditOp) => void;
  /** 反推提示词（P-B1）：从当前图解析 prompt 填入 */
  onReverse?: () => void;
  /** 高清超分（P-B1）：派生一张放大图 */
  onUpscale?: () => void;
  /** 裁剪（P-B2）：按比例客户端裁切派生 */
  onCrop?: (ratio: string) => void;
  /** 多视角（P-B2）：单图派生多角度视图 */
  onMultiView?: () => void;
  /** 灯光（P-B3）：按方位叠加光源派生 */
  onRelight?: (preset: string) => void;
  /** 绘制（P-B3）：打开标注画布 */
  onDraw?: () => void;
  /** 主体卡额外动作（如“生成三视图”），渲染在动作条内 */
  extraActions?: ReactNode;
  /** 生成按钮文案（主体卡用“生成参考图”） */
  generateLabel?: string;
  /** “生成prompt”悬停提示（主体卡用“用名称+描述拼提示词”） */
  composeHint?: string;
}

export function ImageGenControls({
  node,
  generating,
  hasUpstream,
  onParams,
  onGenerate,
  onOptimize,
  onCompose,
  onUpload,
  onPickVariant,
  onEdit,
  onReverse,
  onUpscale,
  onCrop,
  onMultiView,
  onRelight,
  onDraw,
  extraActions,
  generateLabel = '生成',
  composeHint,
}: ImageGenControlsProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [cropOpen, setCropOpen] = useState(false);
  const [lightOpen, setLightOpen] = useState(false);
  const p = node.params;
  const model = findImageModel(p?.model) ?? IMAGE_MODELS[0];
  if (model === undefined) {
    return null;
  }
  const credit = p !== undefined ? estimateImageCredit(model, p.resolution, p.count) : 0;
  const disc = formatDiscount(model.discount);
  const countOptions = Array.from({ length: model.maxCount }, (_, i) => i + 1);

  const changeModel = (id: string) => {
    if (p === undefined) {
      return;
    }
    const m = findImageModel(id);
    if (m === undefined) {
      return;
    }
    const patch: Partial<GenerationParams> = { model: id };
    if (!m.resolutions.includes(p.resolution)) {
      patch.resolution = m.resolutions[0] ?? '1080P';
    }
    if (!m.aspectRatios.includes(p.aspectRatio)) {
      patch.aspectRatio = m.aspectRatios[0] ?? '1:1';
    }
    if (p.count > m.maxCount) {
      patch.count = m.maxCount;
    }
    onParams(patch);
  };

  return (
    <div className="flex flex-col gap-2">
      {/* ① 模型 + 参数 + 成本：同一视角 */}
      <div className="flex items-center gap-2">
        <span className="shrink-0 text-uiSm text-ink-faint">模型</span>
        <select className={`${sel} flex-1`} value={model.id} onChange={(e) => changeModel(e.target.value)}>
          {IMAGE_MODELS.map((m) => (
            <option key={m.id} value={m.id}>
              {m.label}
            </option>
          ))}
        </select>
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-uiSm">
        <label className="flex items-center gap-1 text-ink-faint">
          比例
          <select
            className={sel}
            value={p?.aspectRatio ?? '16:9'}
            onChange={(e) => onParams({ aspectRatio: e.target.value as GenerationParams['aspectRatio'] })}
          >
            {model.aspectRatios.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-1 text-ink-faint">
          分辨率
          <select className={sel} value={p?.resolution ?? '1080P'} onChange={(e) => onParams({ resolution: e.target.value })}>
            {model.resolutions.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-1 text-ink-faint">
          数量
          <select className={sel} value={String(p?.count ?? 1)} onChange={(e) => onParams({ count: Number(e.target.value) })}>
            {countOptions.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="rounded-control bg-surface-base px-2.5 py-1.5 text-uiSm text-ink-secondary">
        预计消耗 <span className="font-num font-semibold text-accent">{credit}</span> 积分
        {disc !== '' && <span className="ml-1 text-state-info">限时 {disc}</span>}
        {model.note !== undefined && <span className="ml-1 text-ink-faint">· {model.note}</span>}
      </div>

      {/* ② 提示词 + 生成prompt（绿）/ AI优化（蓝） */}
      <div className="flex items-center gap-3">
        <span className="text-uiSm text-ink-faint">提示词</span>
        <div className="flex items-center gap-3">
          <button
            type="button"
            className={`${promptBtn} text-state-ok glow-hover`}
            disabled={generating || !hasUpstream}
            title={composeHint ?? (hasUpstream ? '承接上游脚本对角色/场景/道具的描写生成' : '需先连一条来自上游（分镜/文本）的连线')}
            onClick={onCompose}
          >
            <Sparkles size={13} />
            生成prompt
          </button>
          <button
            type="button"
            className={`${promptBtn} text-state-info glow-hover`}
            disabled={generating}
            title="在当前提示词基础上 AI 重写"
            onClick={onOptimize}
          >
            <Wand2 size={13} />
            AI优化
          </button>
        </div>
      </div>
      <textarea
        value={p?.prompt ?? ''}
        placeholder="自定义提示词：主体、场景、光线、构图、风格…（或点上方“生成prompt / AI优化”）"
        className={`${field} h-28 shrink-0 resize-none`}
        onChange={(e) => onParams({ prompt: e.target.value })}
      />
      {model.supportsNegative && (
        <textarea
          value={p?.negativePrompt ?? ''}
          placeholder="负向提示词（不想要：模糊、水印、变形…）"
          className={`${field} min-h-14 shrink-0 resize-y leading-snug`}
          onChange={(e) => onParams({ negativePrompt: e.target.value })}
        />
      )}

      {/* ③ 结果预览：主图 + 悬停工具栏 + 多张候选 */}
      {node.media !== undefined && (
        <div className="group/pv relative overflow-hidden rounded-control border border-surface-line">
          <img src={node.media.thumbnail ?? node.media.url} alt={node.title} className="block w-full" />
          <div className="absolute inset-x-0 bottom-0 flex justify-center gap-1 bg-black/55 p-1 opacity-0 transition-opacity group-hover/pv:opacity-100">
            <button type="button" className={toolBtn} disabled={generating} onClick={() => onEdit('repaint')}>
              重绘
            </button>
            <button type="button" className={toolBtn} disabled={generating} onClick={() => onEdit('outpaint')}>
              扩图
            </button>
            <button type="button" className={toolBtn} disabled={generating} onClick={() => onEdit('cutout')}>
              抠图
            </button>
            {onReverse !== undefined && (
              <button type="button" className={toolBtn} disabled={generating} onClick={onReverse}>
                反推
              </button>
            )}
            {onUpscale !== undefined && (
              <button type="button" className={toolBtn} disabled={generating} onClick={onUpscale}>
                高清
              </button>
            )}
            {onMultiView !== undefined && (
              <button type="button" className={toolBtn} disabled={generating} onClick={onMultiView}>
                多视角
              </button>
            )}
            {onCrop !== undefined && (
              <button type="button" className={toolBtn} disabled={generating} onClick={() => setCropOpen((v) => !v)}>
                裁剪
              </button>
            )}
            {onCrop !== undefined &&
              cropOpen &&
              CROP_RATIOS.map((r) => (
                <button
                  key={r}
                  type="button"
                  className={toolBtn}
                  onClick={() => {
                    onCrop(r);
                    setCropOpen(false);
                  }}
                >
                  {r}
                </button>
              ))}
            {onRelight !== undefined && (
              <button type="button" className={toolBtn} disabled={generating} onClick={() => setLightOpen((v) => !v)}>
                灯光
              </button>
            )}
            {onRelight !== undefined &&
              lightOpen &&
              LIGHT_PRESETS.map((lp) => (
                <button
                  key={lp}
                  type="button"
                  className={toolBtn}
                  onClick={() => {
                    onRelight(lp);
                    setLightOpen(false);
                  }}
                >
                  {lp}
                </button>
              ))}
            {onDraw !== undefined && (
              <button type="button" className={toolBtn} disabled={generating} onClick={onDraw}>
                绘制
              </button>
            )}
          </div>
        </div>
      )}
      {node.variants !== undefined && node.variants.length > 1 && (
        <div className="flex gap-1 overflow-x-auto">
          {node.variants.map((v) => (
            <button
              key={v.mediaId}
              type="button"
              title="设为主要结果"
              onClick={() => onPickVariant(v)}
              className={`shrink-0 overflow-hidden rounded-control border ${node.media?.mediaId === v.mediaId ? 'border-accent' : 'border-surface-line'}`}
            >
              <img src={v.thumbnail ?? v.url} alt="" className="block h-14 w-14 object-cover" />
            </button>
          ))}
        </div>
      )}

      {/* ④ 底部动作条 */}
      <div className="flex flex-wrap items-center gap-2 text-ui">
        <button
          type="button"
          className="inline-flex items-center gap-1 font-semibold text-accent hover:underline disabled:opacity-50"
          disabled={generating}
          onClick={onGenerate}
        >
          {generating ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}
          {generating ? '生成中…' : generateLabel}
        </button>
        {extraActions}
        <span className="text-ink-faint">·</span>
        <button type="button" className={linkItem} onClick={() => fileRef.current?.click()}>
          上传
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f !== undefined) {
              onUpload(f);
            }
            e.currentTarget.value = '';
          }}
        />
      </div>
    </div>
  );
}
